import React, { useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { Page, Text, BlockStack, Box, Card } from '@shopify/polaris';
import Barcode from '#client/components/Barcode/Barcode';
import wwLogo from '#client/assets/images/ww-logo-allcompany-black.png';
import './PublicVoucher.css';
import './PrintableVoucher.css';

/**
 * PrintableVoucher component (kept for any UI usage)
 * Renders one or more vouchers into a printable format.
 */
function PrintableVoucher({ vouchers = [], onRenderComplete }) {
    const pdfRef = useRef();
    const qrRefs = useRef([]);
    // Safe check before using vouchers
    const companyName = vouchers?.[0]?.program?.companyName;

    // On mount, generate QR codes and optionally trigger a render completion callback
    useEffect(() => {
        vouchers.forEach((voucher, index) => {
            if (qrRefs.current[index]) {
                const publicUrl = `${window.location.origin}/voucher/${voucher.voucherCode}/${voucher.uuid}`;
                QRCode.toCanvas(qrRefs.current[index], publicUrl, { width: 200 }, (err) => {
                    if (err) console.error('QR Code error:', err);
                });
            }
        });
        // Delay ensures DOM and QR codes are fully rendered before continuing
        if (onRenderComplete) {
            const timeout = setTimeout(onRenderComplete, 150);
            return () => clearTimeout(timeout);
        }
    }, [vouchers, onRenderComplete]);
    // Return early if no vouchers are provided
    if (!Array.isArray(vouchers) || vouchers.length === 0) {
        return <div>No vouchers to print.</div>;
    }

    return (
        <Box className="printable-voucher-wrapper printable-voucher-reset-transform" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
            <Page fullWidth>
                <div ref={pdfRef} className="pdf-wrapper">
                    <BlockStack gap="600">
                        {vouchers.map((voucher, index) => {
                            const { voucherCode, firstName = '', lastName = '', distEmailAddress = '', dateStart, dateExpires, program = {} } = voucher;
                            const description = program?.programDescription ?? 'No description provided';

                            return (
                                <React.Fragment key={voucherCode + index}>
                                    <Box className="voucher-card">
                                        <Card sectioned>
                                            <BlockStack gap="400">
                                                {/* Header message */}
                                                <Box padding="400" style={{ textAlign: 'center' }} gap="300">
                                                    <img src={wwLogo} alt="Work World Logo" style={{ width: 'var(--p-size-500)' }} />
                                                </Box>

                                                <Box paddingBlockStart="400" paddingBlockEnd="600" style={{ textAlign: 'center' }}>
                                                    <Text variant="headingXl" fontWeight="bold">
                                                        {firstName && lastName && companyName
                                                            ? `Hi ${firstName} ${lastName}, you have received a Work World Voucher from ${companyName}!`
                                                            : "You've received a Work World Voucher!"}
                                                    </Text>
                                                    <Text variant="headingLg" fontWeight="bold">
                                                        Redeem this at any Work World location or online at workworld.com
                                                    </Text>
                                                </Box>

                                                {/* Two column layout */}
                                                <Box
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'flex-start',
                                                        gap: '3rem',
                                                        margin: '0 auto',
                                                        padding: '1rem 2rem',
                                                    }}
                                                >
                                                    {/* LEFT - Voucher Details */}
                                                    <Box style={{ flexBasis: '65%', flexShrink: 0 }}>
                                                        <BlockStack gap="200">
                                                            {/* Product Discount */}
                                                            {program?.discountAmount != null && Number(program.discountAmount) > 0 && (
                                                                <Text variant="headingLg" fontWeight="regular">
                                                                    <strong>Product Discount:</strong>{' '}
                                                                    {program.discountType === 'percentage' ? `${program.discountAmount}%` : `$${Number(program.discountAmount).toFixed(2)} off`}
                                                                </Text>
                                                            )}

                                                            {/* Program Credit – FIXED VERSION */}
                                                            {program?.subsidyType && (
                                                                <Text variant="headingLg" fontWeight="regular">
                                                                    <strong>Program Credit:</strong> {/* Percentage-based subsidy programs */}
                                                                    {['percentage', 'percentage-subsidy'].includes(program.subsidyType) && `${Number(voucher.valueInitial)}%`}
                                                                    {/* Total-value programs */}
                                                                    {program.subsidyType === 'total-value' && `$${Number(voucher.valueInitial).toFixed(2)}`}
                                                                </Text>
                                                            )}

                                                            {/* Recipient */}
                                                            {(firstName || lastName) && (
                                                                <Text variant="headingLg" fontWeight="regular">
                                                                    <strong>Recipient:</strong> {firstName} {lastName}
                                                                </Text>
                                                            )}

                                                            {/* Free Shipping */}
                                                            {program?.freeShipping && program.freeShipping !== 'false' && (
                                                                <Text variant="headingLg" fontWeight="regular">
                                                                    <strong>Free Shipping:</strong> Included
                                                                </Text>
                                                            )}

                                                            {/* Tax Exempt */}
                                                            {program?.taxExempt && program.taxExempt !== 'false' && (
                                                                <Text variant="headingLg" fontWeight="regular">
                                                                    <strong>Tax Exempt:</strong> Included
                                                                </Text>
                                                            )}

                                                            {/* Voucher Code */}
                                                            <Text variant="headingLg" fontWeight="regular">
                                                                <strong>Voucher Code:</strong> {voucherCode}
                                                            </Text>

                                                            {/* Email */}
                                                            {distEmailAddress && (
                                                                <Text variant="headingLg" fontWeight="regular">
                                                                    <strong>Email:</strong> {distEmailAddress}
                                                                </Text>
                                                            )}

                                                            {/* Start Date */}
                                                            <Text variant="headingLg" fontWeight="regular">
                                                                <strong>Start Date:</strong> {new Date(dateStart).toLocaleDateString()}
                                                            </Text>

                                                            {/* Expiration Date */}
                                                            <Text variant="headingLg" fontWeight="regular">
                                                                <strong>Expiration Date:</strong> {new Date(dateExpires).toLocaleDateString()}
                                                            </Text>

                                                            {/* Description */}
                                                            {description && (
                                                                <Text variant="headingLg" fontWeight="regular">
                                                                    <strong>Program Description:</strong> {description}
                                                                </Text>
                                                            )}
                                                        </BlockStack>
                                                    </Box>

                                                    {/* RIGHT SIDE – QR + BARCODE */}
                                                    <Box style={{ flexBasis: '35%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                                        <Text variant="bodyMd">Scan to view your voucher online.</Text>
                                                        <canvas ref={(el) => (qrRefs.current[index] = el)} />
                                                        <Barcode code={voucherCode} />
                                                    </Box>
                                                </Box>
                                            </BlockStack>
                                        </Card>
                                    </Box>
                                    {(index + 1) % 3 !== 0 && index !== vouchers.length - 1 && <hr style={{ border: '1px solid #ccc', margin: '2rem 0' }} />}
                                </React.Fragment>
                            );
                        })}
                    </BlockStack>
                </div>
            </Page>
        </Box>
    );
}

export default PrintableVoucher;
